/**
 * Contract of the AI provision for US/MX bills (ALC-4305, AP-30). Approval moves the bill to
 * PROCESSING_APPROVAL with the posting PENDING; it becomes APPROVED in the same transaction that
 * posts its provision: the agent's journal when it validates, otherwise the deterministic one.
 * When the ledger refuses both, the bill returns to SUBMITTED with the posting REFUSED, to be
 * corrected and sent again. The close team reviews AI journals before the period closes. The API
 * and the web client both read these.
 */

export const AI_POSTING_STATUSES = [
  'NOT_APPLICABLE',
  'PENDING',
  'POSTED',
  'FALLBACK',
  'REFUSED',
] as const;
export type AiPostingStatus = (typeof AI_POSTING_STATUSES)[number];

export const AI_POSTING_REVIEW_STATUSES = ['UNREVIEWED', 'REVIEWED'] as const;
export type AiPostingReviewStatus = (typeof AI_POSTING_REVIEW_STATUSES)[number];

export const AI_POSTING_LINE_SIDES = ['DEBIT', 'CREDIT'] as const;
export type AiPostingLineSide = (typeof AI_POSTING_LINE_SIDES)[number];

export const AI_POSTING_LINE_ROLES = [
  'EXPENSE',
  'ASSET',
  'AP_VENDOR',
  'TAX_RECOVERABLE',
  'TAX_WITHHELD_LIABILITY',
  'OTHER',
] as const;
export type AiPostingLineRole = (typeof AI_POSTING_LINE_ROLES)[number];

export const AI_POSTING_TAX_CODES = ['IVA', 'ISR'] as const;
export type AiPostingTaxCode = (typeof AI_POSTING_TAX_CODES)[number];

export const AI_POSTING_FALLBACK_STAGES = [
  'TIMEOUT',
  'INPUT',
  'AGENT',
  'VALIDATION',
  'CONFIDENCE',
  'WORKER',
  'RECLASSIFIED',
  'FALLBACK',
  'DEFERRED_LEGS',
] as const;
export type AiPostingFallbackStage = (typeof AI_POSTING_FALLBACK_STAGES)[number];

/** Below this confidence the deterministic provision posts instead (decision #26). */
export const AI_POSTING_MIN_CONFIDENCE = 0.7;
/** Fixed agent deadline and pending-attempt timeout, in seconds. */
export const AI_POSTING_AGENT_DEADLINE_SECONDS = 90;
export const AI_POSTING_PENDING_TIMEOUT_SECONDS = 120;

export interface AiPostingLineInput {
  accountId: string;
  side: AiPostingLineSide;
  /** Decimal string in the document currency. */
  amount: string;
  role: AiPostingLineRole;
  memo: string;
  /** The agent's reason for this line; absent on lines a reviewer changed. */
  rationale?: string | null;
  /** Which tax a withheld-tax line belongs to; set by the agent, kept through corrections. */
  taxCode?: AiPostingTaxCode | null;
  classificationId?: string | null;
}

export interface AiPostingLine extends AiPostingLineInput {
  accountCode: string;
  accountName: string;
  dimensions?: string[];
  /** ACC-12: a cost line on an account the bill's classification did not choose. */
  divergesFromClassification?: boolean;
}

export interface AiPostingJournal {
  journalId: string;
  bookId: string;
  periodId: string;
  currency: string;
  lines: AiPostingLine[];
  rationale: string;
  /** What the threshold compared: the agent's report scaled by the cost share that kept the
   * classification accounts (ACC-12). */
  confidence: number;
  /** The agent's own report. */
  reportedConfidence: number;
  warnings: string[];
  model: string;
  promptVersion: string;
  postedAt: string;
  correctedBy: string | null;
  correctedAt: string | null;
}

export interface AiPostingFallback {
  code: string;
  message: string;
  stage: AiPostingFallbackStage;
  /** The engine's refusal behind a failed deterministic provision, when there is one. */
  refusalCode: string | null;
  /** Original AI/input refusal when the deterministic provision also failed. */
  aiRefusalCode?: string | null;
}

/** A payment leg the ledger refused after the provision posted; the provision stays. */
export interface AiPostingPaymentLegRefusal {
  code: string;
  paymentId: string | null;
}

export interface AiPostingReview {
  status: AiPostingReviewStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

export interface AiPostingState {
  status: AiPostingStatus;
  /** Why the deterministic provision posted instead (FALLBACK), or why it could not (PENDING). */
  fallback: AiPostingFallback | null;
  /** The active AI journal; only for POSTED. */
  posting: AiPostingJournal | null;
  /** Only for POSTED. */
  review: AiPostingReview | null;
  /** POSTED, bill APPROVED, nothing paid or settled yet, period open. */
  correctable: boolean;
  /** A provision whose payment legs the ledger refused; `retry` posts them again. */
  paymentLegRefusal?: AiPostingPaymentLegRefusal | null;
  /** PENDING after the deterministic fallback also failed (the agent runs again), or a paid
   * payment of a POSTED/FALLBACK bill with no ledger leg, refused or not (the legs post again). */
  retryable: boolean;
}

export interface AiPostingCorrection {
  lines: AiPostingLineInput[];
}

export const AI_POSTING_LIST_SORT_FIELDS = [
  'competencia',
  'amount',
  'vendorName',
  'postedAt',
] as const;
export type AiPostingListSortField = (typeof AI_POSTING_LIST_SORT_FIELDS)[number];

export const AI_POSTING_LIST_REVIEW_FILTERS = ['UNREVIEWED', 'REVIEWED', 'ALL'] as const;
export type AiPostingListReviewFilter = (typeof AI_POSTING_LIST_REVIEW_FILTERS)[number];

export const AI_POSTING_LIST_STATUS_FILTERS = ['POSTED', 'FALLBACK', 'ALL'] as const;
export type AiPostingListStatusFilter = (typeof AI_POSTING_LIST_STATUS_FILTERS)[number];

/** Query of GET /ap/ai-postings. On the wire `vendorIds` is comma-separated. */
export interface AiPostingListQuery {
  entityId?: string;
  bookId?: string;
  periodId?: string;
  review?: AiPostingListReviewFilter;
  status?: AiPostingListStatusFilter;
  vendorIds?: string[];
  /** Contains, case-insensitive. */
  vendorBillNumber?: string;
  /** YYYY-MM-DD, inclusive. */
  billCompetencyDateFrom?: string;
  billCompetencyDateTo?: string;
  /** A bare YYYY-MM-DD is a UTC day; an ISO instant is compared with the posting time. */
  postedDateFrom?: string;
  postedDateTo?: string;
  /** Decimal strings compared with the bill total. */
  totalAmountEq?: string;
  totalAmountGt?: string;
  totalAmountGte?: string;
  totalAmountLt?: string;
  totalAmountLte?: string;
  sortBy?: AiPostingListSortField;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  /** 1–200, default 50. */
  pageSize?: number;
}

export interface AiPostingListItem {
  billId: string;
  vendorBillNumber: string | null;
  vendorName: string | null;
  entityId: string;
  entityName: string | null;
  /** ISO 3166-1 alpha-2 of the entity. */
  entityCountry: string;
  amount: string;
  currency: string;
  /** Competência, YYYY-MM-DD. */
  competencia: string | null;
  status: Exclude<AiPostingStatus, 'NOT_APPLICABLE'>;
  /** The live AI journal, sent back on review so a stale row cannot review a newer one. */
  journalId: string | null;
  confidence: number | null;
  /** ACC-12: some cost of the AI journal is on an account the classification did not choose. */
  classificationDivergence: boolean;
  postedAt: string | null;
  reviewStatus: AiPostingReviewStatus | null;
  fallbackCode: string | null;
}

export interface AiPostingListResponse {
  items: AiPostingListItem[];
  /** Every matching bill; `items` stops at the page size. */
  total: number;
  /** Matching bills per review tab, with every filter applied except the review one. */
  reviewCounts: Record<AiPostingListReviewFilter, number>;
}
